import{ useEffect, useState } from "react";
import { toast } from "react-hot-toast";
import AxiosInstance  from "../context/axiosInstance";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronRight, Loader2, SearchX } from "lucide-react";


const ServicesByCategory = ({ category, onSelectService }) => {

  const [services, setServices] = useState([]);
  const [isLoading, setIsLoading] = useState(true);


  useEffect(() => {
    const fetchServices = async () => {
      setIsLoading(true);
      try {
        const response = await AxiosInstance.get(
          `/services/?category=${encodeURIComponent(category)}`
        );
        setServices(response.data.services || []);
      } catch (err) {
        console.error("Error fetching services:", err);
        toast.error("Failed to fetch services.");
        setServices([]);
      } finally {
        setIsLoading(false);
      }
    };

    if (category) {
      fetchServices();
    }
  }, [category]); // eslint-disable-line react-hooks/exhaustive-deps

  // Animation variants for the list
  const containerVariants = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.08,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    show: { opacity: 1, y: 0 },
  };

  return (
    // The component is now a clean content block, inheriting its background.
    <div className="w-full">
      <div className="mb-6 md:mb-8">
        <h2 className="text-2xl md:text-3xl font-bold tracking-tighter text-white">
          {category} Services
        </h2>
        <p className="text-sm text-gray-400 mt-1">
          Select a service to view more details and book.
        </p>
      </div>

      <AnimatePresence mode="wait">
        {isLoading ? (
          <motion.div
            key="loader"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="flex flex-col items-center justify-center min-h-[40vh] text-gray-400"
          >
            <Loader2 size={32} className="animate-spin mb-4 text-primary-500" />
            <p className="font-semibold">Loading Services...</p>
          </motion.div>
        ) : services.length === 0 ? (
          <motion.div
            key="empty"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="flex flex-col items-center justify-center min-h-[40vh] text-center text-gray-500"
          >
            <SearchX size={40} className="mb-4 text-gray-600" />
            <p className="font-bold text-xl text-white">No Services Found</p>
            <p>There are currently no services available in this category.</p>
          </motion.div>
        ) : (
          <motion.div
            key="list"
            variants={containerVariants}
            initial="hidden"
            animate="show"
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4"
          >
            {services.map((service) => {
               return (
                <motion.div
                  key={service.service_id}
                  variants={itemVariants}
                  className="group relative p-5 rounded-2xl cursor-pointer border border-white/10 bg-white/5 hover:bg-white/10 transition-all duration-300 shadow-lg"
                  onClick={() => onSelectService(service)}
                >
                  <div className="flex justify-between items-start mb-4">
                    <h3 className="text-lg font-bold text-white tracking-tight group-hover:text-primary-300 transition-colors">
                      {service.name}
                    </h3>
                    <div className="bg-primary-500/10 border border-primary-500/20 px-2 py-1 rounded-md">
                      <span className="text-primary-300 font-bold text-xs">
                        {service.currency} {service.price}
                      </span>
                    </div>
                  </div>

                  <p className="text-gray-400 text-xs line-clamp-2 mb-4 leading-relaxed">
                    {service.description}
                  </p>
                  
                  <div className="flex items-center gap-2 text-primary-400 font-bold text-xs uppercase tracking-wider opacity-0 group-hover:opacity-100 transition-opacity">
                    <span className="border-b border-primary-400/30">View Details</span>
                    <ChevronRight size={14} className="transform group-hover:translate-x-1 transition-transform" />
                  </div>
                </motion.div>
              );
            })}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ServicesByCategory;
